// src/components/RequireAuth.js
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase/config';

export default function RequireAuth({ children }) {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // NEW: org-deletion state. Tracked via a separate org-doc subscription
  // that lives alongside the existing user-doc subscription.
  const [isOrgDeleted, setIsOrgDeleted] = useState(false);
  const [isBillingOwner, setIsBillingOwner] = useState(false);

  // s8portal-4e7b2c95 — the browser holds a CUSTOMER portal session (cust-… uid), e.g. a staff
  // member opened a customer's portal link in this browser, which replaces the staff session in
  // EVERY tab. Staff pages can't work with that session (every read is permission-denied, and a
  // fresh tab used to sit on "Loading workspace…" forever), so send them to the login page. We do
  // NOT sign out: that would also end the customer's session in the portal tab.
  const [isPortalSession, setIsPortalSession] = useState(false);

  const location = useLocation();

  useEffect(() => {
    // s8portal-4e7b2c95 — the staff listeners belong to the EFFECT, not to one auth callback.
    // Before, the callback returned its cleanup to onAuthStateChanged, which ignores return values,
    // so a session swap left the old user's user-doc and org listeners running (permission-denied
    // spam). Every auth change now tears down the previous user's listeners first.
    let unsubscribeSnapshot = null;
    let unsubscribeOrg = null;
    let currentOrgId = null;
    const teardownStaffListeners = () => {
      if (unsubscribeSnapshot) { unsubscribeSnapshot(); unsubscribeSnapshot = null; }
      if (unsubscribeOrg) { unsubscribeOrg(); unsubscribeOrg = null; }
      currentOrgId = null;
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      teardownStaffListeners();

      if (!user) {
        setIsPortalSession(false);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      setIsAuthenticated(true);

      // Portal customers (cust-* uids, minted only by exchangePortalToken) have no users doc and
      // no business in the staff app: stop here, before any staff read, and redirect (below).
      const isPortalUser = user.uid?.startsWith('cust-');
      if (isPortalUser) {
        setIsPortalSession(true);
        setLoading(false);
        return;
      }
      setIsPortalSession(false);

      // --- CRM STAFF LOGIC ---
      // Only internal staff reach this point.
      const userRef = doc(db, 'users', user.uid);

      // Record sign-in server-side: stamps lastLoginAt and flips invited->active
      // for BOTH web and mobile (moved out of the client into the recordSignIn
      // Cloud Function). Fire-and-forget — access is gated on organizationId,
      // never on status, so login never waits on it. seam1-recordsignin-7b2e4d16
      httpsCallable(functions, 'recordSignIn')().catch(() => {});

      const subscribeToOrg = (orgId) => {
        if (!orgId) {
          // No org to watch — clean up if we had one.
          if (unsubscribeOrg) { unsubscribeOrg(); unsubscribeOrg = null; }
          currentOrgId = null;
          setIsOrgDeleted(false);
          setIsBillingOwner(false);
          return;
        }
        if (currentOrgId === orgId) {
          // Already watching the right org — no-op.
          return;
        }
        // Different org (or first time) — tear down old subscription, start fresh.
        if (unsubscribeOrg) { unsubscribeOrg(); unsubscribeOrg = null; }
        currentOrgId = orgId;
        unsubscribeOrg = onSnapshot(
          doc(db, 'organizations', orgId),
          (orgSnap) => {
            if (!orgSnap.exists()) {
              // Org doc gone — treat as deleted (defensive; org docs shouldn't disappear unexpectedly).
              setIsOrgDeleted(true);
              setIsBillingOwner(false);
              return;
            }
            const orgData = orgSnap.data();
            const deleted = orgData.status === 'deleted';
            setIsOrgDeleted(deleted);
            setIsBillingOwner(deleted && orgData.ownerUid === user.uid);
          },
          (error) => {
            console.warn('RequireAuth: org snapshot error:', error.code);
            // Don't lock the user out on a transient permission error — fail open.
            setIsOrgDeleted(false);
            setIsBillingOwner(false);
          }
        );
      };

      unsubscribeSnapshot = onSnapshot(userRef, (docSnap) => {
        if (!docSnap.exists()) {
           console.error("RequireAuth: User logged in, but no Firestore doc found!");
           setIsSubscribed(false);
           setLoading(false);
           return;
        }

        const userData = docSnap.data();

        // 1. SESSION CHECK (Highlander Rule)
        const dbWebSessionId    = userData.currentWebSessionId;
        const localWebSessionId = localStorage.getItem('crm_web_session_id');

        if (dbWebSessionId && localWebSessionId && dbWebSessionId !== localWebSessionId) {
            console.warn("RequireAuth: Web session displaced — signed in on another browser.");
            signOut(auth);
            return;
        }

        // NEW: Subscribe to (or update subscription on) the user's org doc.
        // Uses optional chaining so missing organizationId clears any active sub.
        subscribeToOrg(userData.organizationId || null);

        // 2. SUBSCRIPTION / ACCESS CHECK
        const isPayer = ['Owner', 'Owner/System Admin', 'System Admin', 'Owner / System Admin'].includes(userData.role);

        if (isPayer) {
            // Always check the ORGANIZATION's subscription via the original owner's user doc.
            // This ensures any Owner/Admin added to an org gets access as long as the org is active.
            const orgId = userData.organizationId;
            if (!orgId) {
                setIsSubscribed(false);
                setLoading(false);
                return;
            }

            // If this user has their own subscription status, use it directly
            if (userData.subscriptionStatus) {
                setIsSubscribed(userData.subscriptionStatus === 'active' || userData.subscriptionStatus === 'trialing');
                setLoading(false);
                return;
            }

            // Otherwise find the org's original owner and use their status
            getDoc(doc(db, 'organizations', orgId)).then(async orgSnap => {
                if (!orgSnap.exists()) {
                    setIsSubscribed(false);
                    setLoading(false);
                    return;
                }
                const ownerUid = orgSnap.data().ownerUid;
                if (!ownerUid) {
                    setIsSubscribed(false);
                    setLoading(false);
                    return;
                }
                try {
                    const ownerSnap = await getDoc(doc(db, 'users', ownerUid));
                    if (ownerSnap.exists()) {
                        const status = ownerSnap.data().subscriptionStatus;
                        setIsSubscribed(status === 'active' || status === 'trialing');
                    } else {
                        setIsSubscribed(false);
                    }
                } catch {
                    setIsSubscribed(false);
                }
                setLoading(false);
            }).catch(() => {
                setIsSubscribed(false);
                setLoading(false);
            });
            return;
        } else {
            // Staff members bypass the paywall if they belong to an organization
            if (userData.organizationId) {
                setIsSubscribed(true);
            } else {
                setIsSubscribed(false);
            }
        }

        setLoading(false);
      }, (error) => {
        console.error("RequireAuth Snapshot Error:", error);
        setIsSubscribed(false);
        setLoading(false);
      });
    });

    return () => {
      unsubscribeAuth();
      teardownStaffListeners();
    };
  }, []);

  if (loading) {
    return (
        <div className="flex h-screen items-center justify-center">
            <div className="text-gray-500">Loading workspace permissions...</div>
        </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // s8portal-4e7b2c95 — a customer portal session is not a staff login: go sign in as staff.
  if (isPortalSession) {
    return <Navigate to="/login" state={{ from: location, portalSession: true }} replace />;
  }

  // NEW: Deleted-org gate. Must come BEFORE the subscription check, because a
  // deleted org typically also has a cancelled subscription — without this
  // ordering, the user would be sent to /settings?tab=billing instead of
  // /org-deleted, and the deletion state would never be visible.
  if (isOrgDeleted) {
      // Allow access to the dedicated org-deleted route; redirect everywhere else.
      if (location.pathname === '/org-deleted') {
          return children;
      }
      return <Navigate to="/org-deleted" replace />;
  }

  // --- The "Smart" Paywall ---
  if (!isSubscribed) {
    if (location.pathname.startsWith('/settings')) {
        return children;
    }
    return <Navigate to="/settings?tab=billing" replace />;
  }

  return children;
}